import config from "@/config/constants";
import { trackEvent } from "@/utils/analytics/analytics";
import { LOGIN_MFA_CODE_RESENT } from "@/utils/analyticsEvents";

export const TWO_FACTOR_RESEND_COOLDOWN_SECONDS = 30;

export const TWO_FACTOR_RESEND_SUCCESS_MESSAGE =
  "Code resent to the mobile number we have on file for you";

export const TWO_FACTOR_RESEND_ERROR_MESSAGE =
  "Unable to resend the code. Please try again.";

export const TWO_FACTOR_SEND_INVALID_PHONE_MESSAGE =
  "Please enter a valid mobile number.";

export const TWO_FACTOR_SEND_NETWORK_ERROR_MESSAGE =
  "There's an issue using your phone number, please contact support: support@spectrum.life";

export const MFA_SESSION_EXPIRED_MESSAGE =
  "Your session has expired. Please sign in again.";

export type InFlightGuard = {
  run: <Result>(task: () => Promise<Result>) => Promise<Result | undefined>;
  isInFlight: () => boolean;
};

export type TwoFactorChallenge = {
  temporaryToken: string;
  oathToken: string;
  phone: string;
  phoneLast4: string;
  skipInitialCooldown: boolean;
  initialError: string;
};

export const EMPTY_TWO_FACTOR_CHALLENGE: TwoFactorChallenge = {
  temporaryToken: "",
  oathToken: "",
  phone: "",
  phoneLast4: "",
  skipInitialCooldown: false,
  initialError: "",
};

export const TWO_FACTOR_CHALLENGE_STORAGE_KEY = "slTwoFactorChallenge";

export type TwoFactorChallengeStorage = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
};

export const isEmptyTwoFactorChallenge = (
  challenge: TwoFactorChallenge | null | undefined,
): boolean =>
  !challenge?.temporaryToken && !challenge?.oathToken && !challenge?.phone;

const sanitizeTwoFactorChallenge = (value: unknown): TwoFactorChallenge => {
  if (!value || typeof value !== "object") {
    return { ...EMPTY_TWO_FACTOR_CHALLENGE };
  }
  const candidate = value as Partial<TwoFactorChallenge>;
  return {
    temporaryToken: String(candidate.temporaryToken || ""),
    oathToken: String(candidate.oathToken || ""),
    phone: String(candidate.phone || ""),
    phoneLast4: String(candidate.phoneLast4 || ""),
    skipInitialCooldown: Boolean(candidate.skipInitialCooldown),
    initialError: String(candidate.initialError || ""),
  };
};

export const getTwoFactorChallengeStorage = (): TwoFactorChallengeStorage | null => {
  try {
    if (typeof sessionStorage === "undefined") {
      return null;
    }
    return sessionStorage;
  } catch {
    return null;
  }
};

export const readStoredTwoFactorChallenge = (
  storage: TwoFactorChallengeStorage | null = getTwoFactorChallengeStorage(),
): TwoFactorChallenge => {
  if (!storage) {
    return { ...EMPTY_TWO_FACTOR_CHALLENGE };
  }
  try {
    const raw = storage.getItem(TWO_FACTOR_CHALLENGE_STORAGE_KEY);
    if (!raw) {
      return { ...EMPTY_TWO_FACTOR_CHALLENGE };
    }
    return sanitizeTwoFactorChallenge(JSON.parse(raw));
  } catch {
    return { ...EMPTY_TWO_FACTOR_CHALLENGE };
  }
};

export const persistTwoFactorChallenge = (
  challenge: TwoFactorChallenge,
  storage: TwoFactorChallengeStorage | null = getTwoFactorChallengeStorage(),
): void => {
  if (!storage) {
    return;
  }
  try {
    const nextChallenge = sanitizeTwoFactorChallenge(challenge);
    if (isEmptyTwoFactorChallenge(nextChallenge)) {
      storage.removeItem(TWO_FACTOR_CHALLENGE_STORAGE_KEY);
      return;
    }
    storage.setItem(
      TWO_FACTOR_CHALLENGE_STORAGE_KEY,
      JSON.stringify(nextChallenge),
    );
  } catch {
    // Private browsing can throw; the in-memory Vuex copy still covers this page.
  }
};

export const queryOauthToken = (oauthQuery: unknown): string =>
  typeof oauthQuery === "string" ? oauthQuery : "";

export const isChallengeForCurrentSession = (
  oauthQuery: unknown,
  challenge: TwoFactorChallenge | null | undefined,
): boolean => {
  if (!challenge || isEmptyTwoFactorChallenge(challenge)) {
    return false;
  }
  const oauth = queryOauthToken(oauthQuery);
  if (oauth) {
    return oauth === challenge.temporaryToken || oauth === challenge.oathToken;
  }
  return Boolean(challenge.oathToken && challenge.phone);
};

export const resolveActiveChallenge = (
  oauthQuery: unknown,
  challenge: TwoFactorChallenge | null | undefined,
): TwoFactorChallenge =>
  isChallengeForCurrentSession(oauthQuery, challenge)
    ? sanitizeTwoFactorChallenge(challenge)
    : { ...EMPTY_TWO_FACTOR_CHALLENGE };

export type TwoFactorResendResult = {
  success: boolean;
  error: string | null;
  token: string;
  waitSeconds: number | null;
};

type JsonResponse = {
  data: {
    success?: boolean;
    two_factor?: boolean | number;
    token?: string;
    err?: string;
    phone_last4?: string;
  };
};

export type TwoFactorPoster = (
  url: string,
  payload: Record<string, string>,
  options: unknown,
) => Promise<JsonResponse>;

export const createInFlightGuard = (): InFlightGuard => {
  let inFlight = false;

  const run = async <Result>(
    task: () => Promise<Result>,
  ): Promise<Result | undefined> => {
    if (inFlight) {
      return undefined;
    }
    inFlight = true;
    try {
      return await task();
    } finally {
      inFlight = false;
    }
  };

  return {
    run,
    isInFlight: () => inFlight,
  };
};

export const ensureE164Phone = (phone: string): string => {
  const trimmed = String(phone || "").trim();
  if (!trimmed) {
    return trimmed;
  }
  if (trimmed.startsWith("+")) {
    return `+${trimmed.slice(1).replace(/\D/g, "")}`;
  }
  return `+${trimmed.replace(/\D/g, "")}`;
};

export const phoneLast4FromE164 = (phone: string): string => {
  const digits = String(phone).replace(/\D/g, "");
  return digits.length >= 4 ? digits.slice(-4) : "";
};

export const parseResendWaitSeconds = (
  errorMessage: string | null,
): number | null => {
  if (!errorMessage) {
    return null;
  }
  const waitMatch = errorMessage.match(/(\d+)\s*second/i);
  return waitMatch ? Number(waitMatch[1]) : null;
};

export const resendFailureCooldownSeconds = (
  waitSeconds: number | null | undefined,
): number =>
  waitSeconds && waitSeconds > 0
    ? waitSeconds
    : TWO_FACTOR_RESEND_COOLDOWN_SECONDS;

export const resendCodeLabel = (
  cooldownSeconds: number,
  isResending: boolean,
): string => {
  if (cooldownSeconds > 0) {
    return `Resend code (${cooldownSeconds}s)`;
  }
  if (isResending) {
    return "Sending...";
  }
  return "Resend code";
};

export const resolveVerifyToken = (
  oauthQuery: unknown,
  challenge: TwoFactorChallenge | null | undefined,
): string => queryOauthToken(oauthQuery) || challenge?.temporaryToken || "";

export const buildTwoFactorRoute = (
  challenge: TwoFactorChallenge,
): { path: string; query: Record<string, string> } => {
  const query: Record<string, string> = {};
  if (challenge.temporaryToken) {
    query.oauth = challenge.temporaryToken;
  }
  if (challenge.phoneLast4) {
    query.phone_last4 = challenge.phoneLast4;
  }
  return { path: "/two-factor", query };
};

export const sendTwoFactorCode = async (
  token: string,
  phone: string,
  post: TwoFactorPoster,
): Promise<JsonResponse["data"]> => {
  try {
    const response = await post(
      config.api_env + "/application/api-hc/two-factor-send",
      { token, phone: ensureE164Phone(phone) },
      config.options,
    );
    return response.data || { success: false };
  } catch {
    return {
      success: false,
      err: TWO_FACTOR_SEND_NETWORK_ERROR_MESSAGE,
    };
  }
};

export const resendTwoFactorCode = async (
  token: string,
  post: TwoFactorPoster,
): Promise<TwoFactorResendResult> => {
  try {
    const response = await post(
      config.api_env + "/application/api-hc/two-factor-resend",
      { token },
      config.options,
    );
    if (response.data.success === true) {
      trackEvent(LOGIN_MFA_CODE_RESENT, { success: "Yes" });
      return {
        success: true,
        error: null,
        token: response.data.token || token,
        waitSeconds: null,
      };
    }
    trackEvent(LOGIN_MFA_CODE_RESENT, { success: "No" });
    const error = response.data.err || TWO_FACTOR_RESEND_ERROR_MESSAGE;
    return {
      success: false,
      error,
      token: "",
      waitSeconds: parseResendWaitSeconds(error),
    };
  } catch {
    trackEvent(LOGIN_MFA_CODE_RESENT, { success: "No" });
    return {
      success: false,
      error: TWO_FACTOR_RESEND_ERROR_MESSAGE,
      token: "",
      waitSeconds: null,
    };
  }
};
